import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { AppProvider, useApp } from './context/AppContext';
import { createAppTheme } from './config/theme';
import Layout from './components/common/Layout';
import ScrollToTop from './components/common/ScrollToTop';

// Pages
import Landing from './pages/common/Landing';
import Authentication from './pages/common/Authentication';
import Dashboard from './pages/common/Dashboard';
import Profile from './pages/common/Profile';

// Super Admin pages
import AddStore from './pages/superAdmin/AddStore';
import StoreList from './pages/superAdmin/StoreList';
import FocusErrors from './pages/superAdmin/FocusErrors';
import PodsManagement from './pages/superAdmin/PodsManagement';
import AddUser from './pages/superAdmin/AddUser';
import UserList from './pages/superAdmin/UserList';
import ViewUser from './pages/superAdmin/ViewUser';
import CustomerSegmentation from './pages/superAdmin/CustomerSegmentation';

import OrderList from './pages/superAdmin/OrderList';
import ViewOrder from './pages/superAdmin/ViewOrder';
import ManualOrderCreation from './pages/superAdmin/ManualOrderCreation';
import NotificationsManager from './pages/superAdmin/NotificationsManager';
import AddCoupon from './pages/superAdmin/AddCoupon';
import CouponList from './pages/superAdmin/CouponList';
import EditCoupon from './pages/superAdmin/EditCoupon';
import TimeSlots from './pages/superAdmin/delivery/TimeSlots';
import AddProduct from './pages/superAdmin/AddProduct';
import ProductList from './pages/superAdmin/ProductList';
import EditProduct from './pages/superAdmin/EditProduct';
import ViewProduct from './pages/superAdmin/ViewProduct';
import Stock from './pages/superAdmin/Stock';
import CategoryManagement from './pages/superAdmin/CategoryManagement';
import AppWebsiteBanners from './pages/superAdmin/AppWebsiteBanners';
import AppWebsitePopupBanners from './pages/superAdmin/AppWebsitePopupBanners';
import CartRecommendations from './pages/superAdmin/CartRecommendations';
import AppWebsiteNotifications from './pages/superAdmin/AppWebsiteNotifications';

// Admin pages
import StoreAdminDashboard from './pages/admin/StoreAdminDashboard';
import StoreAdminOrders from './pages/admin/StoreAdminOrders';
import AdminReports from './pages/admin/Reports';
import AdminDeliveryReport from './pages/admin/DeliveryReport';

// Import Super Admin Reports
import SuperAdminReports from './pages/superAdmin/Reports';
import SuperAdminDeliveryReport from './pages/superAdmin/DeliveryReport';
import RiderRouteDistanceReport from './pages/superAdmin/RiderRouteDistanceReport';
import OrderStatusDashboard from './pages/superAdmin/OrderStatusDashboard';
import OrderOntimeDashboard from './pages/superAdmin/OrderOntimeDashboard';

// Import Delivery Module Components
import Riders from './pages/delivery/Riders';
import RiderAppUpdates from './pages/delivery/RiderAppUpdates';
import DeliveryOrders from './pages/delivery/DeliveryOrders';
import DeliveryRoutes from './pages/delivery/Routes';
import LiveTracking from './pages/delivery/LiveTracking';

// Protected Route Component
const ProtectedRoute = ({ children, requireAuth = true, allowedRoles = [] }) => {
  const { state } = useApp();
  
  if (!state.isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  // Check if specific roles are required
  if (allowedRoles.length > 0) {
    const userRole = state.user?.role;
    const hasPermission = allowedRoles.some(role => {
      // Handle both underscore and hyphen formats
      if (role === 'super_admin') {
        return userRole === 'super_admin' || userRole === 'super-admin';
      }
      return userRole === role;
    });
    
    if (!hasPermission) {
      return <Navigate to="/dashboard" replace />;
    }
  }
  
  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { state } = useApp();
  
  if (state.isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// App Content Component
const AppContent = () => {
  const theme = createAppTheme();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <Layout>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route 
              path="/auth" 
              element={
                <PublicRoute>
                  <Authentication />
                </PublicRoute>
              } 
            />

            {/* Protected Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/super-admin/orders/manual-create"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <ManualOrderCreation />
                </ProtectedRoute>
              }
            />

            {/* Super Admin Routes */}
            <Route 
              path="/super-admin/stores/add" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <AddStore />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/stores/list" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super_admin', 'super-admin']}>
                  <StoreList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/stores" 
              element={<Navigate to="/super-admin/stores/list" replace />} 
            />
            <Route 
              path="/super-admin/focus-errors"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <FocusErrors />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/super-admin/stores/pods" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <PodsManagement />
                </ProtectedRoute>
              } 
            />

            {/* User Management Routes */}
            <Route 
              path="/super-admin/users/add" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <AddUser />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/users/list" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <UserList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/users/view/:userId" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <ViewUser />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/users/segmentation" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <CustomerSegmentation />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/super-admin/users" 
              element={<Navigate to="/super-admin/users/list" replace />} 
            />

            {/* Order Management Routes */}
            <Route 
              path="/super-admin/orders/list" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <OrderList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/orders/view/:orderId" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <ViewOrder />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/orders" 
              element={<Navigate to="/super-admin/orders/list" replace />} 
            />

            {/* Notification Management Routes */}
            <Route 
              path="/super-admin/notifications/manage" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <NotificationsManager />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/notifications" 
              element={<Navigate to="/super-admin/notifications/manage" replace />} 
            />

            {/* App & Website */}
            <Route
              path="/super-admin/app-website/banners"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <AppWebsiteBanners />
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin/app-website/popup-banners"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <AppWebsitePopupBanners />
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin/app-website/cart-recommendations"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <CartRecommendations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin/app-website/notifications"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <AppWebsiteNotifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/super-admin/app-website"
              element={<Navigate to="/super-admin/app-website/banners" replace />}
            />

            {/* Category Management Routes */}
            <Route 
              path="/super-admin/categories/manage" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <CategoryManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/categories" 
              element={<Navigate to="/super-admin/categories/manage" replace />} 
            />

            {/* Product Management Routes */}
            <Route 
              path="/super-admin/products/add" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <AddProduct />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/products/list" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <ProductList />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/super-admin/products/stock"
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <Stock />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/super-admin/products/edit/:id" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <EditProduct />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/products/view/:id" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <ViewProduct />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/products" 
              element={<Navigate to="/super-admin/products/list" replace />} 
            />

            {/* Coupon Management Routes */}
            <Route 
              path="/super-admin/coupons/add" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <AddCoupon />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/coupons/list" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <CouponList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/coupons/edit/:couponId" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <EditCoupon />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/coupons" 
              element={<Navigate to="/super-admin/coupons/list" replace />} 
            />

            {/* Delivery Management Routes */}
            <Route 
              path="/super-admin/delivery/time-slots" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <TimeSlots />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/delivery/orders" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <DeliveryOrders />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/delivery/routes" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <DeliveryRoutes />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/delivery/riders" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <Riders />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/delivery/live-tracking" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <LiveTracking />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/delivery/rider-app-updates" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <RiderAppUpdates />
                </ProtectedRoute>
              } 
            />

            {/* Reports Routes */}
            <Route 
              path="/super-admin/reports/sales" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <SuperAdminReports />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/reports/delivery" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <SuperAdminDeliveryReport />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/reports/rider-route-distance" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <RiderRouteDistanceReport />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/reports/order-status" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <OrderStatusDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin/reports/order-ontime" 
              element={
                <ProtectedRoute allowedRoles={['super_admin', 'super-admin']}>
                  <OrderOntimeDashboard />
                </ProtectedRoute>
              } 
            />

            {/* Placeholder routes for footer links */}
            <Route path="/privacy" element={<div>Privacy Policy - Coming Soon</div>} />
            <Route path="/terms" element={<div>Terms of Service - Coming Soon</div>} />
            <Route path="/contact" element={<div>Contact Us - Coming Soon</div>} />
            <Route path="/about" element={<div>About - Coming Soon</div>} />

            {/* Store Admin Routes */}
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'store_admin']}>
                  <StoreAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/orders" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'store_admin']}>
                  <StoreAdminOrders />
                </ProtectedRoute>
              } 
            />
                        <Route 
              path="/admin/reports/sales" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'store_admin']}>
                  <AdminReports />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin/reports/delivery" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'store_admin']}>
                  <AdminDeliveryReport />
                </ProtectedRoute>
              }
            />

            {/* Admin Delivery Routes */}
            <Route 
              path="/admin/delivery/orders" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'store_admin']}>
                  <DeliveryOrders />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/delivery/routes" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'store_admin']}>
                  <DeliveryRoutes />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/delivery/riders" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'store_admin']}>
                  <Riders />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/delivery/live-tracking" 
              element={
                <ProtectedRoute allowedRoles={['admin', 'store_admin']}>
                  <LiveTracking />
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/admin" 
              element={<Navigate to="/admin/dashboard" replace />} 
            />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </Router>
    </ThemeProvider>
  );
};

// Main App Component
function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}

export default App;
